import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BTN_PRIMARY, CARD, INPUT, MUTED } from "@/lib/theme";
import { ShieldCheck } from "lucide-react";

export default function Auth({ mode }) {
  const isRegister = mode === "register";
  const { authSet } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "customer" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = isRegister ? "/auth/register" : "/auth/login";
      const payload = isRegister ? form : { email: form.email, password: form.password };
      const { data } = await api.post(path, payload);
      authSet(data);
      toast.success(isRegister ? "Welcome aboard!" : "Welcome back!");
      const r = data.user.role;
      navigate(r === "owner" ? "/owner" : r === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const label = "block text-sm font-semibold text-[#17324D] mb-1.5";

  return (
    <div className="App min-h-screen bg-[#FFF4E6] text-[#17324D]">
      <Navbar />
      <div className="max-w-md mx-auto px-5 py-16 md:py-20">
        <div className={`${CARD} p-8 md:p-10 fade-up`}>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight leading-[1.08]">{isRegister ? "Join The Regulars Club" : "Welcome Back"}</h1>
          <p className={`${MUTED} mt-2 mb-8`}>{isRegister ? "Create your account to get started." : "Log in to your account."}</p>
          <form onSubmit={submit} className="space-y-5">
            {isRegister && (
              <div>
                <label className={label}>Full Name</label>
                <input data-testid="auth-name-input" required value={form.name} onChange={set("name")} className={INPUT} />
              </div>
            )}
            <div>
              <label className={label}>Email</label>
              <input data-testid="auth-email-input" type="email" required value={form.email} onChange={set("email")} className={INPUT} />
            </div>
            <div>
              <label className={label}>Password</label>
              <input data-testid="auth-password-input" type="password" required value={form.password} onChange={set("password")} className={INPUT} />
            </div>
            {isRegister && (
              <>
                <div>
                  <label className={label}>Phone (for text updates)</label>
                  <input data-testid="auth-phone-input" value={form.phone} onChange={set("phone")} placeholder="610-395-7006" className={INPUT} />
                </div>
                <div>
                  <label className={label}>I am a...</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[["customer", "Customer"], ["owner", "Restaurant Owner"]].map(([v, l]) => (
                      <button type="button" key={v} data-testid={`auth-role-${v}`} onClick={() => setForm({ ...form, role: v })}
                        className={`py-2.5 rounded-xl font-semibold text-sm border transition-colors ${form.role === v ? "bg-[#17324D] text-white border-[#17324D]" : "bg-white border-[#17324D]/20 hover:border-[#17324D]"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <button data-testid="auth-submit-btn" disabled={loading} className={`${BTN_PRIMARY} w-full`}>
              {loading ? "Please wait..." : isRegister ? "Create Account" : "Log In"}
            </button>
          </form>
          <p className={`text-sm text-center mt-6 ${MUTED}`}>
            {isRegister ? "Already a member? " : "New here? "}
            <Link to={isRegister ? "/login" : "/register"} data-testid="auth-switch-link" className="text-[#D94A3D] font-semibold underline underline-offset-4">
              {isRegister ? "Log in" : "Create an account"}
            </Link>
          </p>
          <p className={`text-xs text-center mt-4 ${MUTED}`}>
            By continuing you agree to our{" "}
            <Link to="/terms" data-testid="auth-terms-link" className="underline underline-offset-2">Terms</Link> and{" "}
            <Link to="/privacy" data-testid="auth-privacy-link" className="underline underline-offset-2">Privacy Policy</Link>.
          </p>
        </div>
        <p className={`mt-6 text-xs text-center ${MUTED} flex items-center justify-center gap-1.5`}>
          <ShieldCheck size={14} className="text-[#D94A3D]" /> Payments secured by Stripe · Cancel anytime
        </p>
      </div>
    </div>
  );
}
